import React from 'react';
import { Controller } from 'react-hook-form';
import ReactSelect, { GroupBase, StylesConfig } from 'react-select';

import { FormError } from '../FormElements/FormElements';
import { customStyles } from '../Select/Select';

const defaultStyles = {
  ...customStyles,
  option: (provided: any, state: any) => {
    let backgroundColor: string | undefined;
    if (state.isSelected) backgroundColor = '#628fb9';
    else if (state.isFocused) backgroundColor = 'rgba(98, 143, 185, 0.15)';
    return {
      ...customStyles.option(provided),
      backgroundColor,
      color: state.isSelected ? '#fff' : provided.color,
      ':active': {
        backgroundColor: '#628fb9',
        color: '#fff',
      },
    };
  },
  groupHeading: (provided: any) => ({
    ...provided,
    fontWeight: 600,
    fontSize: '11px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: '#628fb9',
    padding: '8px 16px 4px',
  }),
};

type GroupedSelectProps<TOption extends { value: any }, TGroup extends GroupBase<TOption>> = {
  name: string;
  control: any;
  options: TGroup[];
  isSearchable?: boolean;
  placeholder?: string;
  maxMenuHeight?: number;
  styles?: StylesConfig<TOption, false, TGroup>;
  className?: string;
  errorClassName?: string;
  onOptionChange?: (option: TOption | null) => void;
  inputId?: string;
};

export const GroupedSelect = <TOption extends { value: any }, TGroup extends GroupBase<TOption>>({
  name,
  control,
  options,
  isSearchable,
  placeholder,
  maxMenuHeight,
  styles,
  className,
  errorClassName,
  onOptionChange,
  inputId,
}: GroupedSelectProps<TOption, TGroup>) => (
  <Controller
    name={name}
    control={control}
    render={({ field, fieldState: { error } }) => {
      const selectedOption =
        (options.flatMap(g => g.options as TOption[]).find(o => o.value === field.value) as TOption) ?? null;
      return (
        <div className={className}>
          <ReactSelect<TOption, false, TGroup>
            inputId={inputId}
            options={options}
            isSearchable={isSearchable}
            placeholder={placeholder}
            maxMenuHeight={maxMenuHeight}
            value={selectedOption}
            onChange={option => {
              field.onChange((option as TOption | null)?.value ?? null);
              onOptionChange?.(option as TOption | null);
            }}
            styles={styles ?? (defaultStyles as StylesConfig<TOption, false, TGroup>)}
          />
          {error && (
            <FormError
              className={errorClassName}
              error={error.message}
            />
          )}
        </div>
      );
    }}
  />
);
